import { FC, Fragment, ReactNode } from 'react';
import {
  AddedFontsCard,
  addedFontsCard,
  HeaderBarsCard,
  headerBarsCard
} from '../../helpers/fontChangesHelpers';
import { FraudTrendEvidenceResult } from '../../hooks/useFraudTrendEvidence';
import {
  BarRow,
  FontCheckLabel,
  KeyBox,
  KeyGrid,
  ListCheckEmptyState,
  OverviewCard,
  OverviewCardBody,
  OverviewCardHead,
  OverviewCardList,
  OverviewResult
} from '../../styles';
import { withBars } from './HeaderBarsTab';
import ListCheckChip from './ListCheckChip';
import { listCheckCopy } from './listCheckCopy';

interface FontChangesOverviewProps {
  evidence?: FraudTrendEvidenceResult;
}

interface CardProps {
  title: string;
  testId: string;
  fired: boolean | null;
  children: ReactNode;
}

const copy = listCheckCopy.fontChanges;

const Card: FC<CardProps> = ({ title, testId, fired, children }) => (
  <OverviewCard $hit={fired === true} data-testid={testId}>
    <OverviewCardHead $hit={fired === true}>
      {title}
      {fired !== null && (
        <ListCheckChip
          chip={
            fired
              ? { tone: 'bad', text: copy.chip.potentialEdit }
              : { tone: 'neutral', text: copy.chip.looksNormal }
          }
        />
      )}
    </OverviewCardHead>
    <OverviewCardBody>
      {fired === null ? <ListCheckEmptyState>{copy.couldNotCheck}</ListCheckEmptyState> : children}
    </OverviewCardBody>
  </OverviewCard>
);

const keyBoxes = (keys: string[], moved: number[] = []): ReactNode[] =>
  keys.map((key, index) => (
    <KeyBox
      key={key}
      $moved={moved.includes(index)}
      data-testid={moved.includes(index) ? 'font-changes-card-moved-key' : undefined}
    >
      {key}
    </KeyBox>
  ));

const AddedFonts: FC<{ card: AddedFontsCard | null }> = ({ card }) => {
  const expected = (
    <>
      <FontCheckLabel>{copy.expectedKeyOrder}</FontCheckLabel>
      {keyBoxes(card?.expected ?? [])}
    </>
  );

  return (
    <Card
      title={copy.addedFontsTab}
      testId="font-changes-card-added-fonts"
      fired={card?.fired ?? null}
    >
      <KeyGrid $columns={Math.max(card?.expected.length ?? 0, 1)}>
        {card?.fired && card.others.length ? (
          <>
            {expected}
            {card.others.map((order) => (
              <Fragment key={order.keys.join(' ')}>
                <FontCheckLabel $bad>{copy.documentFonts(order.fonts)}</FontCheckLabel>
                {keyBoxes(order.keys, order.moved)}
              </Fragment>
            ))}
          </>
        ) : (
          <>
            <FontCheckLabel>{copy.allFontsInOrder(card?.total ?? 0)}</FontCheckLabel>
            {keyBoxes(card?.expected ?? [])}
          </>
        )}
      </KeyGrid>
    </Card>
  );
};

const resultLine = ({ rule, fired, source, retyped, total }: HeaderBarsCard): string => {
  if (!fired) return copy.headerBarsAsExpected(total, rule.expected);
  return source === 'bars'
    ? copy.barsRetyped(retyped, total, rule.retyped, rule.expected)
    : copy.headerBarsRetyped(retyped, total, rule.retyped, rule.expected);
};

const bars = (sign: string, count: number, bad = false): ReactNode[] =>
  Array.from({ length: count }, (_, index) => (
    <KeyBox
      key={`${sign}-${index}`}
      $moved={bad}
      data-testid={bad ? 'font-changes-card-retyped-bar' : undefined}
    >
      {sign}
    </KeyBox>
  ));

const HeaderBars: FC<{ card: HeaderBarsCard | null }> = ({ card }) => (
  <Card
    title={copy.headerBarsTab}
    testId="font-changes-card-header-bars"
    fired={card?.fired ?? null}
  >
    {card && (
      <>
        <OverviewResult data-testid="font-changes-card-header-result">
          {withBars(resultLine(card), card.rule)}
        </OverviewResult>
        <FontCheckLabel>{copy.expectedStatement}</FontCheckLabel>
        <BarRow>{bars(card.rule.expected, card.total)}</BarRow>
        <FontCheckLabel $bad={card.retyped > 0}>{copy.documentBars(card.total)}</FontCheckLabel>
        <BarRow>
          {bars(card.rule.expected, card.total - card.retyped)}
          {bars(card.rule.retyped, card.retyped, true)}
        </BarRow>
      </>
    )}
  </Card>
);

const FontChangesOverview: FC<FontChangesOverviewProps> = ({ evidence }) => {
  if (evidence?.status === 'loading') {
    return <ListCheckEmptyState>{copy.overviewLoading}</ListCheckEmptyState>;
  }
  if (evidence?.status !== 'success') {
    return <ListCheckEmptyState>{copy.overviewUnavailable}</ListCheckEmptyState>;
  }

  return (
    <OverviewCardList data-testid="font-changes-overview-cards">
      <AddedFonts card={addedFontsCard(evidence.data)} />
      <HeaderBars card={headerBarsCard(evidence.data)} />
    </OverviewCardList>
  );
};

export default FontChangesOverview;
